import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Building2, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}

export default function MyCompanies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/patient/companies").then((r) => setItems(r.data || [])).catch(() => toast.error("Could not load your businesses. Please refresh."));

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="my-companies-title">
          Blitzer Businesses
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {user?.email} — every Blitzer business you've checked in with, in one place.
        </p>

        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#0F52BA]" /></div>
        ) : items.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="no-companies">
            You haven't checked in with any Blitz business yet. Open a business's check-in link to get started.
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {items.map((c) => {
              const submitted = c.status === "submitted";
              return (
                <div key={c.clinic_id} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md" data-testid={`company-card-${c.slug || c.clinic_id}`}>
                  <div className="h-1.5 w-full" style={{ backgroundColor: c.accent_color }} />
                  <div className="p-5">
                    <div className="flex items-center gap-3">
                      {c.has_logo ? (
                        <img src={`${API}/clinic/logo/${c.clinic_id}`} alt="" className="h-11 w-11 rounded-lg object-contain" />
                      ) : (
                        <span className="flex h-11 w-11 items-center justify-center rounded-lg text-white" style={{ backgroundColor: c.accent_color }}>
                          <Building2 className="h-5 w-5" />
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-900">{c.clinic_name}</p>
                        <p className="text-xs text-slate-400">Last activity {fmt(c.updated_at)}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${submitted ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {submitted ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                        {submitted ? "Paperwork submitted" : "In progress"}
                      </span>
                      <Button size="sm" style={{ backgroundColor: c.accent_color }} className="text-white hover:opacity-90" onClick={() => navigate(`/dashboard?clinic=${c.clinic_id}`)} data-testid={`open-company-${c.slug || c.clinic_id}`}>
                        Open <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
